import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LoadingState } from '@nocowanie/core/enums';

import { SliceNameEnum } from '@app/enums';
import { MarketingBanner } from '@app/models';

import { fetchMarketingBanner } from '../api-thunks';

export interface CmsState {
    marketingBanner: {
        data: MarketingBanner | null;
        loading: LoadingState;
        error: string | null;
    };
}

const initialState: CmsState = {
    marketingBanner: {
        data: null,
        loading: LoadingState.IDLE,
        error: null,
    },
};

const slice = createSlice({
    name: SliceNameEnum.CmsGlobal,
    initialState,
    reducers: {
        clearMarketingBanner: state => {
            state.marketingBanner.data = null;
            state.marketingBanner.error = null;
            state.marketingBanner.loading = LoadingState.IDLE;
        },
        setMarketingBannerError: (state, action: PayloadAction<string>) => {
            state.marketingBanner.error = action.payload;
            state.marketingBanner.loading = LoadingState.ERROR;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchMarketingBanner.pending, state => {
                state.marketingBanner.loading = LoadingState.PENDING;
                state.marketingBanner.error = null;
            })
            .addCase(fetchMarketingBanner.fulfilled, (state, action) => {
                state.marketingBanner.loading = LoadingState.SUCCESS;
                state.marketingBanner.data = action.payload;
                state.marketingBanner.error = null;
            })
            .addCase(fetchMarketingBanner.rejected, (state, action) => {
                state.marketingBanner.loading = LoadingState.ERROR;
                state.marketingBanner.error = action.payload || 'Failed to fetch marketing banner';
            });
    },
});

const { actions: sliceActions } = slice;

const actions = {
    ...sliceActions,
    fetchMarketingBanner,
};

const selectSelfState = (state: { [SliceNameEnum.CmsGlobal]: CmsState }) => {
    return state[slice.name] ?? initialState;
};

const selectors = {
    selectMarketingBanner: createSelector(selectSelfState, state => state.marketingBanner),
    selectMarketingBannerData: createSelector(selectSelfState, state => state.marketingBanner.data),
    selectMarketingBannerLoading: createSelector(
        selectSelfState,
        state => state.marketingBanner.loading,
    ),
    selectMarketingBannerError: createSelector(
        selectSelfState,
        state => state.marketingBanner.error,
    ),
    selectIsMarketingBannerLoading: createSelector(
        selectSelfState,
        state => state.marketingBanner.loading === LoadingState.PENDING,
    ),
};

export { slice, actions, selectors };
